import React, { useState } from "react";
import { FaExclamationTriangle, FaPaperPlane, FaCamera, FaTimes } from "react-icons/fa";
import imageCompression from "browser-image-compression";

export default function ComplaintForm({ establishmentId, backEnd_request }) {
    const [formData, setFormData] = useState({
        complaintText: "",
        phoneNumber: "",
        wantsTracking: true,
        allowPublicShare: true,
    });
    const [selectedPhotos, setSelectedPhotos] = useState([]); // مصفوفة الصور الأصلية
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState({ type: "", msg: "" });

    // معالجة تغيير الحقول النصية والـ Checkbox
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    // معالجة اختيار الملفات مع التحقق من الامتداد JPG
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);

        // تصفية الملفات لتكون JPG/JPEG فقط
        const jpgFiles = files.filter(file =>
            file.type === "image/jpeg" ||
            file.name.toLowerCase().endsWith(".jpg") ||
            file.name.toLowerCase().endsWith(".jpeg")
        );

        if (jpgFiles.length < files.length) {
            setStatus({ type: "warning", msg: "يرجى اختيار صور بصيغة JPG فقط." });
        } else {
            setStatus({ type: "", msg: "" });
        }

        setSelectedPhotos(prev => [...prev, ...jpgFiles]);
    };

    // حذف صورة من القائمة المختارة
    const removePhoto = (index) => {
        setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
    };

    // إرسال النموذج مع ضغط الصور
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setStatus({ type: "info", msg: "جاري معالجة الصور وضغطها..." });

        const data = new FormData();
        data.append("EstablishmentFieldUid", establishmentId);
        data.append("ComplaintText", formData.complaintText);
        data.append("PhoneNumber", formData.phoneNumber);
        data.append("WantsTracking", formData.wantsTracking);
        data.append("AllowPublicShare", formData.allowPublicShare);

        // إعدادات مكتبة الضغط
        const compressionOptions = {
            maxSizeMB: 1,           // الحجم الأقصى 1 ميجابايت
            maxWidthOrHeight: 1280,  // الأبعاد القصوى
            useWebWorker: true,
            fileType: "image/jpeg"
        };

        try {
            // ضغط كل صورة وإضافتها إلى FormData
            for (const file of selectedPhotos) {
                const compressedFile = await imageCompression(file, compressionOptions);
                // التأكد من الحفاظ على اسم الملف مع امتداد .jpg
                const finalFile = new File([compressedFile], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                    type: "image/jpeg",
                });
                data.append("Photos", finalFile);
            }

            // إرسال الطلب إلى السيرفر
            await backEnd_request.post("/public/complaints", data, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            setStatus({ type: "success", msg: "تم إرسال شكواك بنجاح مع الصور المعالجة." });

            // إعادة تعيين النموذج
            setFormData({ complaintText: "", phoneNumber: "", wantsTracking: true, allowPublicShare: true });
            setSelectedPhotos([]);
        } catch (error) {
            console.error("Submission Error:", error);
            setStatus({ type: "danger", msg: "فشل الإرسال، يرجى المحاولة لاحقاً." });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="card shadow-sm border-0 rounded-4 p-4 mt-4 bg-white text-end" dir="rtl">
            <div className="d-flex align-items-center mb-3 text-danger">
                <FaExclamationTriangle className="ms-2" />
                <h5 className="fw-bold mb-0">تقديم شكوى على المنشأة</h5>
            </div>

            {status.msg && (
                <div className={`alert alert-${status.type} py-2 small shadow-sm`}>
                    {status.type === "info" && <span className="spinner-border spinner-border-sm ms-2"></span>}
                    {status.msg}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                {/* نص الشكوى */}
                <div className="mb-3">
                    <textarea
                        name="complaintText"
                        className="form-control bg-light border-0 text-end shadow-none"
                        rows="3"
                        placeholder="اكتب تفاصيل الشكوى هنا... *"
                        required
                        value={formData.complaintText}
                        onChange={handleChange}
                    ></textarea>
                </div>

                {/* اختيار الصور */}
                <div className="mb-3">
                    <label className="btn btn-outline-secondary btn-sm rounded-pill px-4">
                        <FaCamera className="ms-2" /> إرفاق صور JPG (اختياري)
                        <input
                            type="file"
                            multiple
                            hidden
                            onChange={handleFileChange}
                            accept=".jpg, .jpeg"
                        />
                    </label>

                    {selectedPhotos.length > 0 && (
                        <div className="mt-3 d-flex flex-wrap gap-2 justify-content-start">
                            {selectedPhotos.map((file, index) => (
                                <span key={index} className="badge bg-light text-dark border p-2 d-flex align-items-center rounded-3">
                                    <small className="text-truncate" style={{ maxWidth: '120px' }}>{file.name}</small>
                                    <FaTimes
                                        className="ms-2 text-danger cursor-pointer"
                                        onClick={() => removePhoto(index)}
                                    />
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* رقم الهاتف */}
                <div className="mb-3">
                    <input
                        name="phoneNumber"
                        type="tel"
                        className="form-control bg-light border-0 text-end shadow-none"
                        placeholder="رقم الهاتف (اختياري)"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                    />
                </div>

                {/* خيارات إضافية */}
                <div className="d-flex gap-4 mb-4 justify-content-start border-top pt-3">
                    <div className="form-check p-0">
                        <input
                            name="wantsTracking"
                            type="checkbox"
                            className="form-check-input float-none ms-2"
                            id="track"
                            checked={formData.wantsTracking}
                            onChange={handleChange}
                        />
                        <label className="form-check-label small cursor-pointer" htmlFor="track">متابعة الشكوى</label>
                    </div>
                    <div className="form-check p-0">
                        <input
                            name="allowPublicShare"
                            type="checkbox"
                            className="form-check-input float-none ms-2"
                            id="share"
                            checked={formData.allowPublicShare}
                            onChange={handleChange}
                        />
                        <label className="form-check-label small cursor-pointer" htmlFor="share">نشر عام</label>
                    </div>
                </div>

                {/* زر الإرسال */}
                <button
                    type="submit"
                    className="btn btn-danger w-100 rounded-pill fw-bold py-2 shadow-sm"
                    disabled={submitting}
                >
                    {submitting ? (
                        <span>جاري المعالجة والإرسال...</span>
                    ) : (
                        <><FaPaperPlane className="ms-2" /> إرسال الشكوى</>
                    )}
                </button>
            </form>
        </div>
    );
}